/**
 * Layout Component - Control-plane application shell with sidebar and header
 */

import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { LogOut, Zap, UserRound, Building2, ShieldCheck } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import ThemeToggle from './ThemeToggle';

const navigation = [
  { name: 'Tenants', path: '/tenants', icon: Building2 },
  { name: 'Admins', path: '/admins', icon: ShieldCheck },
];

export default function Layout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { admin, logout } = useAuth();

  const handleSignOut = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="main-container">
      <aside>
        <div className="sidebar flex flex-col">
          <div className="flex gap-3 items-center px-6 w-full">
            <div className="logo flex-center">
              <Zap className="w-[1.4rem] h-[1.56rem] text-white" strokeWidth={2} />
            </div>
            <span className="text-heading-sm font-medium text-gray-900 dark:text-white">
              Control Plane
            </span>
          </div>

          <div className="flex-1 overflow-y-auto mt-9">
            <nav className="space-y-1">
              {navigation.map((item) => {
                const isActive = location.pathname.startsWith(item.path);
                const Icon = item.icon;
                return (
                  <Link
                    key={item.name}
                    to={item.path}
                    className={`flex font-base items-center gap-3 px-8 py-5 transition-all ${
                      isActive
                        ? 'text-primary-DEFAULT dark:text-[#F5F5F5] bg-[#F5F5F5] dark:bg-[#444444]'
                        : 'text-info-DEFAULT'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="font-medium">{item.name}</span>
                  </Link>
                );
              })}
            </nav>
          </div>

          <div className="flex justify-between w-full items-center pl-6 pr-4 h-10">
            <div className="flex gap-3 items-center">
              <div className="w-10 h-10 rounded-full bg-primary-DEFAULT flex items-center justify-center">
                <span className="text-white font-semibold text-sm">
                  <UserRound />
                </span>
              </div>
              <div>
                <p className="font-body-base text-gray-900 dark:text-white truncate">
                  {admin?.name ?? admin?.email ?? 'Admin'}
                </p>
              </div>
            </div>
            <button onClick={handleSignOut} className="cursor-pointer">
              <LogOut className="w-6 h-6 text-[#B3B3B3]" />
            </button>
          </div>
        </div>
      </aside>

      <div className="w-[calc(100%-16.5rem)]">
        <header className="h-16 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between px-6">
          <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
            {navigation.find((item) => location.pathname.startsWith(item.path))?.name ||
              'Control Plane'}
          </h1>
          <ThemeToggle />
        </header>

        <main className="px-6 py-8 h-[calc(100%-4rem)] overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
